import React, { useMemo } from "react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { fmtETShort } from "@/lib/time";

/**
 * Curva de odds NYRA vs BET365 por caballo a lo largo del tiempo.
 * props:
 *   - snapshotsNyra: [{captured_at, program_number, win_odds}]
 *   - snapshotsBet365: [...]
 *   - programNumbers: ["1","2",...] (filtro opcional; default: todos)
 */
export const OddsCurveChart = ({ snapshotsNyra = [], snapshotsBet365 = [], programNumbers = null }) => {
    // Agrupar por tiempo. Cada punto = { t, ["NYRA:1"]: odds, ["BET365:1"]: odds, ... }
    const { data, seriesKeys } = useMemo(() => {
        const byTime = new Map();
        const seen = new Set();

        function add(source, snap) {
            if (!snap.win_odds) return;
            if (programNumbers && !programNumbers.includes(snap.program_number)) return;
            const t = new Date(snap.captured_at).getTime();
            if (!byTime.has(t)) byTime.set(t, { t, tsIso: snap.captured_at });
            const key = `${source}:${snap.program_number}`;
            byTime.get(t)[key] = Number(snap.win_odds);
            seen.add(key);
        }
        (snapshotsNyra || []).forEach((s) => add("NYRA", s));
        (snapshotsBet365 || []).forEach((s) => add("BET365", s));

        const points = Array.from(byTime.values()).sort((a, b) => a.t - b.t);
        return { data: points, seriesKeys: Array.from(seen) };
    }, [snapshotsNyra, snapshotsBet365, programNumbers]);

    if (data.length === 0) {
        return (
            <div className="h-64 flex items-center justify-center text-muted-foreground text-sm" data-testid="race-odds-chart-empty">
                Sin snapshots para graficar.
            </div>
        );
    }

    // Colores por caballo. NYRA usa una paleta, BET365 dashed en ámbar.
    const HORSE_PALETTE = [
        "hsl(var(--nyra))",
        "hsl(var(--chart-3))",
        "hsl(var(--chart-4))",
        "hsl(var(--chart-5))",
        "hsl(var(--primary))",
    ];

    return (
        <div className="h-72 w-full" data-testid="race-odds-chart">
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 15, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                    <XAxis
                        dataKey="t"
                        tickFormatter={(t) => fmtETShort(new Date(t).toISOString())}
                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                        stroke="hsl(var(--border))"
                    />
                    <YAxis
                        tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10, fontFamily: "'Source Code Pro', monospace" }}
                        stroke="hsl(var(--border))"
                        domain={["dataMin", "dataMax"]}
                    />
                    <Tooltip
                        contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: 6,
                            color: "hsl(var(--card-foreground))",
                            fontFamily: "'Source Code Pro', monospace",
                            fontSize: 11,
                        }}
                        labelFormatter={(t) => fmtETShort(new Date(t).toISOString())}
                        formatter={(v, name) => [Number(v).toFixed(2), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {seriesKeys.map((key, i) => {
                        const [source, prog] = key.split(":");
                        const isBet = source === "BET365";
                        const stroke = isBet ? "hsl(var(--bet365))" : HORSE_PALETTE[i % HORSE_PALETTE.length];
                        return (
                            <Line
                                key={key}
                                type="monotone"
                                dataKey={key}
                                name={`${source} #${prog}`}
                                stroke={stroke}
                                strokeWidth={isBet ? 2 : 1.6}
                                strokeDasharray={isBet ? "6 4" : "0"}
                                dot={{ r: isBet ? 3 : 2 }}
                                connectNulls
                                isAnimationActive={false}
                            />
                        );
                    })}
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
